import { InboxResponse, InboxListResponse } from "@/generated";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SpotToast } from "@/components/SpotToast";
import { CaseManagementService } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { DeleteDialog } from "@/components/Modals/DeleteDialog";
import { EntityDialogTitle } from "@/components/Modals/EntityDialogTitle";
import { usePathname, useRouter } from "next/navigation";
import { DATA_KEYS } from "@/utils/useRemoteData";

const InboxDeleteDialog = ({
  isOpen,
  onClose,
  inbox,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  inbox: InboxResponse | InboxListResponse | undefined | null;
  onSuccess?: () => void;
}) => {
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const router = useRouter();

  const deleteInboxMutation = useMutation({
    mutationFn: CaseManagementService.deleteInbox,
    onSuccess: () => {
      SpotToast.success("Inbox deleted successfully");
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.INBOXES, spotGroup?.id],
      });
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.INBOXES_ID] });
      onClose();

      if (inbox?.id && pathname?.includes(`/inbox/${inbox.id}`)) {
        router.push("/cases");
      }

      onSuccess?.();
    },
    onError: () => {
      SpotToast.error("Could not delete inbox, please try again later.");
    },
  });

  const handleDelete = () => {
    if (!inbox?.id) return;

    deleteInboxMutation.mutate({
      path: {
        group_id: spotGroup.id,
        inbox_id: inbox.id,
      },
    });
  };

  return (
    <DeleteDialog
      title={
        <EntityDialogTitle
          action="Delete"
          icon={(inbox?.meta?.icon as string) || "inbox"}
          name={inbox?.name ?? ""}
        />
      }
      text="Are you sure you want to delete this inbox? This action cannot be undone."
      onDelete={handleDelete}
      isOpen={isOpen}
      onOpenChange={onClose}
      isLoading={deleteInboxMutation.status === "pending"}
    />
  );
};

export default InboxDeleteDialog;
